/** The app's "Standings" tab, on the web. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { League, StandingRow } from '@lcc/shared';
import { api } from '../lib/api';
import { Card, Empty, ErrorNote, Loading } from '../components/ui';
import { PageHeader } from '../App';

interface StandingsResponse {
  league: { id: string; name: string };
  schedules: Array<{ scheduleNo: number; standings: StandingRow[] }>;
}

export function MyStandingsPage() {
  const [leagueId, setLeagueId] = useState<string | null>(null);

  const leagues = useQuery({
    queryKey: ['leagues'],
    queryFn: () => api.get<{ data: League[] }>('/leagues').then((r) => r.data),
  });

  const active = leagueId ?? leagues.data?.[0]?.id ?? null;

  const standings = useQuery({
    queryKey: ['standings', active],
    queryFn: () => api.get<StandingsResponse>(`/standings/${active}`),
    enabled: Boolean(active),
  });

  return (
    <>
      <PageHeader title="Standings" sub="Computed from the games, not stored" />
      <div className="content stack">
        {leagues.isLoading && <Loading />}
        {leagues.error && <ErrorNote error={leagues.error} />}

        {leagues.data && leagues.data.length > 0 && (
          <div className="tabs" role="tablist" aria-label="League">
            {leagues.data.map((l) => (
              <button
                key={l.id}
                type="button"
                role="tab"
                aria-selected={l.id === active}
                onClick={() => setLeagueId(l.id)}
              >
                {l.name}
              </button>
            ))}
          </div>
        )}

        {standings.isLoading && <Loading />}
        {standings.error && <ErrorNote error={standings.error} />}

        {standings.data && standings.data.schedules.length === 0 && (
          <Card>
            <Empty title="No results yet">Standings appear as soon as games are scored.</Empty>
          </Card>
        )}

        {standings.data?.schedules.map((sch) => (
          <Card
            key={sch.scheduleNo}
            title={standings.data!.schedules.length > 1
              ? `Schedule ${sch.scheduleNo}`
              : standings.data!.league.name}
          >
            <div className="table-wrap">
              <table className="t tabular">
                <caption className="sr-only">
                  {standings.data!.league.name} standings, schedule {sch.scheduleNo}
                </caption>
                <thead>
                  <tr>
                    <th>#</th><th>Team</th>
                    <th className="num">W</th><th className="num">L</th><th className="num">T</th>
                    <th className="num">Pts</th><th className="num">For</th>
                    <th className="num">Agn</th><th className="num">+/−</th>
                  </tr>
                </thead>
                <tbody>
                  {sch.standings.map((r) => (
                    <tr key={r.teamId}>
                      <td className="mono">{r.rank}</td>
                      <td style={{ fontWeight: r.rank === 1 ? 700 : 500 }}>{r.teamName}</td>
                      <td className="num mono">{r.won}</td>
                      <td className="num mono">{r.lost}</td>
                      <td className="num mono">{r.tied}</td>
                      <td className="num mono"><b>{r.points}</b></td>
                      <td className="num mono">{r.pointsFor}</td>
                      <td className="num mono">{r.pointsAgainst}</td>
                      <td className="num mono muted">
                        {r.plusMinus > 0 ? `+${r.plusMinus}` : r.plusMinus}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}
